import {
  openModalCount,
  modal_open_what,
  topSearchActive,
  suppressKeyHandlers,
  stickyHeaderEnabled,
} from "./globalState";

// Local UI state (only used in this file)
let scrolltotopshow = false;
let button_distance = 0;
let button_sticky = false;
let collection_buttons_title = "";

// Autoresize state keyed by element ID
const autoresizeActive: Record<string, boolean> = {};
import { getDomElement, queryAll } from "./utils/dom";
import {
  ID_SCROLL_TO_TOP,
  ID_AUTORESIZE,
  ID_WINDOW_SIDEBAR,
  ID_COLLECTION_BUTTONS,
  ID_COLLECTION_BUTTONS_TITLE,
  ID_SELECT_POPUP,
  ID_LM_ACTIVE,
} from "./utils/elementIds";
import { urlcheck, msCropHide, prev, next, zoom } from "./features/navigation";
import { msLater } from "./features/settingsSave";
import { closeModalLast as closemodallast } from "./ui/modal";
import { hideitopsearch } from "./features/search";
import { modal } from "./ui/modal";
import { fav } from "./features/collections";
import { load } from "./ui/contentLoader";

const Key = {
  Escape: "Escape",
  Enter: "Enter",
  Space: " ",
  ArrowLeft: "ArrowLeft",
  ArrowUp: "ArrowUp",
  ArrowRight: "ArrowRight",
  ArrowDown: "ArrowDown",
  B: "b",
  C: "c",
  F: "f",
  L: "l",
} as const;

export function initPopstateHandler(): void {
  window.addEventListener("popstate", function () {
    urlcheck();
  });
}

export function initKeyboardHandlers(): void {
  document.addEventListener("keyup", function (e) {
    const key = e.key;
    const shiftKey = e.shiftKey;
    const whata = modal_open_what.split(":");

    // Metadata screening shortcuts
    if (getDomElement("ms-title") && suppressKeyHandlers === 0) {
      if (key === Key.C) {
        const cropOk = getDomElement<HTMLInputElement>("crop-ok");
        if (cropOk) cropOk.checked = true;
        msCropHide();
      }
      if (key === Key.B) {
        const cropNok = getDomElement<HTMLInputElement>("crop-nok");
        if (cropNok) cropNok.checked = true;
        msCropHide();
      }
      if (key === Key.Space && shiftKey) {
        void msLater();
      }
    }

    // ESC handling
    if (key === Key.Escape) {
      if (openModalCount > 0) {
        closemodallast();
      } else if (topSearchActive === 1) {
        hideitopsearch();
      }
    }

    // Image view shortcuts
    if (whata[0] === "view" && openModalCount > 0) {
      const picIdEl = getDomElement<HTMLInputElement>("pic_id");

      if (key === Key.L && picIdEl) {
        modal("addlist:" + picIdEl.value, "dynamic");
      }
      if (key === Key.F && picIdEl) {
        void fav(picIdEl.value);
      }
      if (key === Key.ArrowLeft) {
        const prevEl = getDomElement<HTMLInputElement>("img-prev");
        if (prevEl?.value !== "") prev();
      }
      if (key === Key.ArrowRight) {
        const nextEl = getDomElement<HTMLInputElement>("img-next");
        if (nextEl?.value !== "") next();
      }
      if (key === Key.Space) {
        zoom();
      }
    }
  });
}

/** Toggle a CSS class on an element based on scroll position, only acting on state changes. */
function updateAutoresize(id: string, scrolltop: number): void {
  const el = getDomElement(id);
  if (!el) return;
  const shouldBeActive = scrolltop > 50;
  if (shouldBeActive === (autoresizeActive[id] ?? false)) return;
  el.classList.toggle("autoresize-big", shouldBeActive);
  autoresizeActive[id] = shouldBeActive;
}

/** Check if we're near the bottom and call the loader if so. */
function checkInfiniteScroll(
  flagId: string,
  scrolltop: number,
  windowheight: number,
  documentheight: number,
  loader: () => void
): void {
  const flag = getDomElement<HTMLInputElement>(flagId);
  if (flag?.value !== "1") return;
  const pxleft = documentheight - (scrolltop + windowheight);
  if (pxleft < windowheight * 5) {
    loader();
  }
}

export function initScrollHandlers(): void {
  window.addEventListener(
    "scroll",
    function () {
      const scrolltop = window.scrollY;
      const windowheight = document.documentElement.clientHeight;
      const documentheight = document.documentElement.scrollHeight;

      // Infinite scroll loading
      checkInfiniteScroll(ID_LM_ACTIVE, scrolltop, windowheight, documentheight, load);

      // REMOVED (2026-08): the expired-items infinite scroll. It gated on
      // `#expired_active`, which nothing emits, so it never fired — see the note
      // at the top of features/expired.ts.

      // Scroll to top button
      const showButton = scrolltop > 10;
      if (showButton !== scrolltotopshow) {
        getDomElement(ID_SCROLL_TO_TOP)?.style.setProperty(
          "display",
          showButton ? "inline-block" : "none"
        );
        scrolltotopshow = showButton;
      }

      // Autoresize sidebars
      updateAutoresize(ID_AUTORESIZE, scrolltop);
      updateAutoresize(ID_WINDOW_SIDEBAR, scrolltop);

      // Infinite banner recommendations
      const infBannerEl = getDomElement("inf-banner");
      if (infBannerEl && !getDomElement("lm_infscroll_discreet")) {
        const bannerTop = infBannerEl.getBoundingClientRect().top + scrolltop;
        if (scrolltop > bannerTop) {
          if (collection_buttons_title === "") {
            const titleEl = getDomElement(ID_COLLECTION_BUTTONS_TITLE);
            if (titleEl) {
              collection_buttons_title = titleEl.innerHTML;
              titleEl.innerHTML = "Recommendations";
              queryAll<HTMLElement>(".inf-button-hide").forEach(
                (el) => (el.style.display = "none")
              );
              getDomElement("recommendations-fixed")?.style.setProperty("display", "block");
            }
          }
        } else if (collection_buttons_title !== "") {
          const titleEl = getDomElement(ID_COLLECTION_BUTTONS_TITLE);
          if (titleEl) titleEl.innerHTML = collection_buttons_title;
          collection_buttons_title = "";
          queryAll<HTMLElement>(".inf-button-hide").forEach((el) => (el.style.display = "unset"));
          getDomElement("recommendations-fixed")?.style.setProperty("display", "none");
        }
      }

      // Sticky collection buttons
      const collBtns = getDomElement(ID_COLLECTION_BUTTONS);
      if (collBtns && stickyHeaderEnabled === 1) {
        const buttonsTop = collBtns.getBoundingClientRect().top + scrolltop;

        if (scrolltop > buttonsTop && !button_sticky) {
          if (button_distance === 0) button_distance = buttonsTop;
          collBtns.classList.add("collection-buttons-sticky");
          collBtns.classList.remove("collection-buttons");
          getDomElement(ID_COLLECTION_BUTTONS_TITLE)?.style.setProperty("display", "inline");
          getDomElement(ID_SELECT_POPUP)?.classList.add("select-popup-adjusted");
          getDomElement("sec-search-element")?.classList.add("sec-search-element-sticky");
          button_sticky = true;
        }

        if (button_distance > 0 && scrolltop < button_distance && button_sticky) {
          collBtns.classList.remove("collection-buttons-sticky");
          collBtns.classList.add("collection-buttons");
          getDomElement(ID_COLLECTION_BUTTONS_TITLE)?.style.setProperty("display", "none");
          getDomElement(ID_SELECT_POPUP)?.classList.remove("select-popup-adjusted");
          getDomElement("sec-search-element")?.classList.remove("sec-search-element-sticky");
          button_sticky = false;
        }
      }
    },
    { passive: true }
  );
}

export function initEventHandlers(): void {
  initPopstateHandler();
  initKeyboardHandlers();
  initScrollHandlers();
}
